import { useState, useMemo } from 'react';
import ExpenseItem from './ExpenseItem';
import { CATEGORY_KEYS, EXPENSE_REFUND_CLASS_KEYS, resolveClassification } from '../utils/constants';
import { categoryLabel, normalizeCategory } from '../utils/categoryHelpers';
import SortFilterBar, { labelStyle, inputStyle } from './SortFilterBar';

const SORT_OPTIONS = [
  { value: 'date',   label: 'Date' },
  { value: 'amount', label: 'Amount' },
];

const DATE_BASED = new Set(['day', 'week', 'month', 'year']);

// ── Grouping helpers ──────────────────────────────────────────────────────────

function getWeekStart(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  const dow = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
  return monday.toISOString().slice(0, 10);
}

function getGroupKey(expense, groupBy) {
  switch (groupBy) {
    case 'category': return normalizeCategory(expense.category) || 'Uncategorized';
    case 'location': return expense.location || 'No Location';
    case 'day':      return expense.date || 'No Date';
    case 'week':     return getWeekStart(expense.date) ?? 'No Date';
    case 'month':    return expense.date ? expense.date.slice(0, 7) : 'No Date';
    case 'year':     return expense.date ? expense.date.slice(0, 4) : 'No Date';
    case 'user':     return expense.createdBy || 'Unknown';
    default:         return '';
  }
}

function formatGroupLabel(key, groupBy) {
  if (!key || key === 'No Date' || key === 'No Location' || key === 'Unknown' || key === 'Uncategorized') return key || 'Unknown';
  if (groupBy === 'category') return categoryLabel(key) || key;
  switch (groupBy) {
    case 'day': {
      const d = new Date(key + 'T00:00:00');
      return d.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
    }
    case 'week': {
      const d = new Date(key + 'T00:00:00');
      return 'Week of ' + d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    }
    case 'month': {
      const [y, m] = key.split('-');
      return new Date(+y, +m - 1).toLocaleString(undefined, { month: 'long', year: 'numeric' });
    }
    default: return key;
  }
}

const GROUP_LABEL = {
  category: 'Category', location: 'Location',
  day: 'Day', week: 'Week', month: 'Month', year: 'Year',
  user: 'User',
};

// ── Component ─────────────────────────────────────────────────────────────────

const EMPTY_FILTERS = {
  category: '', location: '', minAmount: '', maxAmount: '',
};

function ExpenseList({ expenses, onExpenseClick, onDeleteExpense, isShared, defaultCurrency, reportName }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sortBy,  setSortBy]  = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [groupBy, setGroupBy] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  const setFilter          = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const clearFilters       = () => setFilters(EMPTY_FILTERS);
  const activeFilterCount  = Object.values(filters).filter(Boolean).length;

  const filtered = useMemo(() => {
    let result = expenses.filter(expense => {
      if (filters.category && normalizeCategory(expense.category) !== filters.category) return false;
      if (filters.location) {
        const loc = (expense.location || '').toLowerCase();
        if (!loc.includes(filters.location.toLowerCase())) return false;
      }
      const amount = expense.convertedTotal ?? expense.total ?? 0;
      if (filters.minAmount !== '' && amount < parseFloat(filters.minAmount)) return false;
      if (filters.maxAmount !== '' && amount > parseFloat(filters.maxAmount)) return false;
      if (dateFrom && expense.date < dateFrom) return false;
      if (dateTo   && expense.date > dateTo)   return false;
      return true;
    });

    return [...result].sort((a, b) => {
      const aVal = sortBy === 'amount' ? (a.convertedTotal ?? a.total ?? 0) : (a.date || '');
      const bVal = sortBy === 'amount' ? (b.convertedTotal ?? b.total ?? 0) : (b.date || '');
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [expenses, filters, dateFrom, dateTo, sortBy, sortDir]);

  const filteredTotals = useMemo(() => {
    let expenses = 0, income = 0;
    for (const item of filtered) {
      const amount = parseFloat(item.convertedTotal ?? item.total ?? 0) || 0;
      const cls = resolveClassification(item.businessClassification, item.isRefund, item.transactionDirection);
      if (EXPENSE_REFUND_CLASS_KEYS.has(cls)) expenses -= amount;
      else if (item.transactionDirection === 'credit') income += amount;
      else expenses += amount;
    }
    return { expenses, income };
  }, [filtered]);

  const grouped = useMemo(() => {
    if (!groupBy) return null;
    const map = new Map();
    for (const e of filtered) {
      const key = getGroupKey(e, groupBy);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    const entries = [...map.entries()];
    // Date-based groups: newest first; everything else: alphabetical
    if (DATE_BASED.has(groupBy)) {
      entries.sort((a, b) => b[0].localeCompare(a[0]));
    } else {
      entries.sort((a, b) => a[0].localeCompare(b[0]));
    }
    return entries.map(([key, exps]) => {
      let expenseTotal = 0, incomeTotal = 0;
      for (const e of exps) {
        const amt = parseFloat(e.convertedTotal ?? e.total) || 0;
        const cls = resolveClassification(e.businessClassification, e.isRefund, e.transactionDirection);
        if (EXPENSE_REFUND_CLASS_KEYS.has(cls)) expenseTotal -= amt;
        else if (e.transactionDirection === 'credit') incomeTotal += amt;
        else expenseTotal += amt;
      }
      return { key, label: formatGroupLabel(key, groupBy), expenses: exps, expenseTotal, incomeTotal };
    });
  }, [filtered, groupBy]);

  const currency = defaultCurrency || 'USD';

  const groupSections = useMemo(() => [
    { options: [
      { value: null,       label: 'None'     },
      { value: 'category', label: 'Category' },
      { value: 'location', label: 'Location' },
      ...(isShared ? [{ value: 'user', label: 'User' }] : []),
    ]},
    { label: 'By Date', options: [
      { value: 'day',   label: 'Day'   },
      { value: 'week',  label: 'Week'  },
      { value: 'month', label: 'Month' },
      { value: 'year',  label: 'Year'  },
    ]},
  ], [isShared]);

  return (
    <div>
      <SortFilterBar
        sortOptions={[
          { value: 'date',   label: 'Date'   },
          { value: 'amount', label: 'Amount' },
        ]}
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateChange={(from, to) => { setDateFrom(from); setDateTo(to); }}
        activeFilterCount={activeFilterCount}
        onClearFilters={clearFilters}
        filterFields={<>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Category</label>
            <select value={filters.category} onChange={e => setFilter('category', e.target.value)} style={inputStyle}>
              <option value="">All categories</option>
              {Object.entries(CATEGORY_KEYS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Location</label>
            <input type="text" placeholder="Search location..." value={filters.location} onChange={e => setFilter('location', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Amount</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input type="number" placeholder="Min" value={filters.minAmount} onChange={e => setFilter('minAmount', e.target.value)} style={{ ...inputStyle, flex: 1 }} min="0" />
              <input type="number" placeholder="Max" value={filters.maxAmount} onChange={e => setFilter('maxAmount', e.target.value)} style={{ ...inputStyle, flex: 1 }} min="0" />
            </div>
          </div>
        </>}
        groupBy={groupBy}
        groupSections={groupSections}
        onGroupChange={setGroupBy}
      />

      {/* Results count */}
      {activeFilterCount > 0 && (
        <div style={{ padding: "0 20px 8px", fontSize: 11, color: "#9ca3af" }}>
          {filtered.length} result{filtered.length !== 1 ? 's' : ''}
          {filteredTotals.income > 0
            ? ` · expenses ${currency} ${filteredTotals.expenses.toFixed(2)} · income ${currency} ${filteredTotals.income.toFixed(2)}`
            : ` · ${currency} ${filteredTotals.expenses.toFixed(2)}`
          }
        </div>
      )}

      {/* List — grouped or flat */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "#888" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🧾</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
            {activeFilterCount > 0 ? "No matching transactions" : "No transactions yet"}
          </div>
          <div style={{ fontSize: 13 }}>
            {activeFilterCount > 0 ? "Try adjusting your filters" : "Start by adding your first expense or importing a statement"}
          </div>
        </div>
      ) : grouped ? (
        <div style={{ padding: "0 20px 16px" }}>
          {grouped.map(group => (
            <div key={group.key}>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "baseline",
                padding: "10px 0 6px",
                borderBottom: "1.5px solid #e5e7eb",
                marginBottom: 8,
              }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#374151", letterSpacing: 0.2 }}>
                  {group.label}
                </span>
                <span style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 600 }}>
                  {group.expenses.length}
                  {group.incomeTotal > 0
                    ? ` · −${currency} ${group.expenseTotal.toFixed(2)} +${currency} ${group.incomeTotal.toFixed(2)}`
                    : ` · ${currency} ${group.expenseTotal.toFixed(2)}`
                  }
                </span>
              </div>
              {group.expenses.map(expense => (
                <ExpenseItem
                  key={expense.id}
                  expense={expense}
                  onClick={onExpenseClick}
                  onDelete={onDeleteExpense}
                />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ padding: "0 20px 16px" }}>
          {filtered.map(expense => (
            <ExpenseItem
              key={expense.id}
              expense={expense}
              onClick={onExpenseClick}
              onDelete={onDeleteExpense}
            />
          ))}
        </div>
      )}

    </div>
  );
}

export default ExpenseList;
